/**
 * Datos fiscales del emisor — `TDS-CORE` Parte 1 §2, `OD-0015 D-02` paso 4 y
 * `D-03`.
 *
 * Dos actos separados a propósito:
 *
 *   Guardar     escribe los datos; si cambian, la API BORRA la confirmación.
 *   Confirmar   casilla + botón: el usuario afirma que son correctos. Sin eso
 *               no habrá emisión (OD-0015 D-03). No se fusiona con Guardar
 *               para ahorrar un clic (OD-0015 D-05).
 *
 * Quién puede, lo decide la API (OWNER + MFA, DC-08/DC-09). Esta pantalla no
 * esconde el formulario a nadie: si la API dice que no, explica por qué y qué
 * hacer, en vez de adivinarlo desde las capabilities.
 */
import { useCallback, useEffect, useState } from "react";
import {
  api,
  ESTADO_VALIDACION,
  type CodigoActividadIae,
  type DatosEmisor,
  type Emisor,
  type ErrorApi,
  type FormaPago,
  type NaturalezaActividad,
  type TipoIdEmisor,
} from "../api/cliente";
import { t } from "../i18n/es";
import { conCodigoPostal } from "../ui/provincia-por-cp";
import { EnlacesComprobarAeat } from "../ui/EnlacesComprobarAeat";
import { AreaTexto, Aviso, Boton, Campo, Casilla, Selector, Tarjeta } from "../ui/primitivas";
import { TarjetaLogo } from "./TarjetaLogo";

export const VACIO: DatosEmisor = {
  legalName: "",
  taxIdType: "NIF_ES_PERSON",
  taxIdValue: "",
  addressLine: "",
  postalCode: "",
  city: "",
  province: "",
  activityNature: "NO_INDICADA",
  siiStatus: null,
  iaeActivityType: null,
  iaeActivitySubtype: null,
  iaeEpigraph: null,
  contactEmail: null,
  contactPhone: null,
  paymentMethod: null,
  iban: null,
  documentFooter: null,
};

const FORMAS_PAGO: ReadonlyArray<{ readonly valor: FormaPago | "NINGUNA"; readonly texto: string }> = [
  { valor: "NINGUNA", texto: t.emisor.formasPago.NINGUNA },
  ...(["TRANSFERENCIA", "DOMICILIACION", "TARJETA", "EFECTIVO", "BIZUM", "OTRO"] as const).map((v) => ({
    valor: v,
    texto: t.emisor.formasPago[v],
  })),
];

export const TIPOS: ReadonlyArray<{ readonly valor: TipoIdEmisor; readonly texto: string }> = [
  { valor: "NIF_ES_PERSON", texto: t.emisor.tipos.NIF_ES_PERSON },
  { valor: "NIE_ES", texto: t.emisor.tipos.NIE_ES },
  { valor: "NIF_ES_ENTITY", texto: t.emisor.tipos.NIF_ES_ENTITY },
];

/** OD-0029 D-03: sólo sirve para SUGERIR retención; no cambia ninguna factura. */
const ACTIVIDADES: ReadonlyArray<{ readonly valor: NaturalezaActividad; readonly texto: string }> = [
  { valor: "NO_INDICADA", texto: t.emisor.actividades.NO_INDICADA },
  { valor: "PROFESIONAL", texto: t.emisor.actividades.PROFESIONAL },
  { valor: "EMPRESARIAL", texto: t.emisor.actividades.EMPRESARIAL },
];

/**
 * OD-0035 A: quien lleva los libros de IVA en el SII queda fuera de
 * VERI*FACTU. "Sin responder" es una opción real: no se presupone un "no".
 */
export const SII: ReadonlyArray<{ readonly valor: "" | "NOT_SII" | "SII"; readonly texto: string }> = [
  { valor: "", texto: t.emisor.sii.SIN_RESPONDER },
  { valor: "NOT_SII", texto: t.emisor.sii.NOT_SII },
  { valor: "SII", texto: t.emisor.sii.SII },
];

/** OD-0063 D-10c: el «Código» de actividad del libro registro. */
const CODIGOS_IAE: ReadonlyArray<{ readonly valor: CodigoActividadIae | ""; readonly texto: string }> = [
  { valor: "", texto: t.emisor.iaeCodigos.NINGUNO },
  ...(["A", "B", "C", "D"] as const).map((v) => ({ valor: v, texto: t.emisor.iaeCodigos[v] })),
];

/** Los «Tipo» de A y de B; C y D no tienen. El valor es lo que se guarda: "01".."06". */
function tiposIae(codigo: CodigoActividadIae | null): ReadonlyArray<{ readonly valor: string; readonly texto: string }> {
  if (codigo !== "A" && codigo !== "B") return [];
  return [
    { valor: "", texto: t.emisor.iaeTipoElige },
    ...Object.entries(t.emisor.iaeTipos[codigo]).map(([valor, texto]) => ({ valor, texto })),
  ];
}

/** Validación 40 del libro: con A01 o D hay que dar el inmueble de cada factura, y Faktelio no lo guarda. */
const pideInmueble = (d: DatosEmisor): boolean =>
  d.iaeActivityType === "D" || (d.iaeActivityType === "A" && d.iaeActivitySubtype === "01");

type Bloqueo = "mfa" | "rol" | null;

export function datosDe(e: Emisor): DatosEmisor {
  return {
    legalName: e.legalName,
    taxIdType: e.taxIdType,
    taxIdValue: e.taxIdValue,
    addressLine: e.addressLine,
    postalCode: e.postalCode,
    city: e.city,
    province: e.province,
    activityNature: e.activityNature,
    siiStatus: e.siiStatus,
    iaeActivityType: e.iaeActivityType,
    iaeActivitySubtype: e.iaeActivitySubtype,
    iaeEpigraph: e.iaeEpigraph,
    contactEmail: e.contactEmail,
    contactPhone: e.contactPhone,
    paymentMethod: e.paymentMethod,
    iban: e.iban,
    documentFooter: e.documentFooter,
  };
}

function fechaLegible(iso: string): string {
  return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}

export function PantallaEmisor({
  hayConexion,
  onIrASeguridad,
  onSesionCaducada,
  onCambio,
}: {
  hayConexion: boolean;
  onIrASeguridad: () => void;
  onSesionCaducada: () => void;
  /** Avisa al panel para que Inicio refleje el estado nuevo. */
  onCambio: () => void;
}) {
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [emisor, setEmisor] = useState<Emisor | null>(null);
  const [datos, setDatos] = useState<DatosEmisor>(VACIO);
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  /**
   * Los datos TAL COMO SE RECHAZARON (hallazgo 13): el error de un campo se
   * deja de ver en cuanto se edita, y vuelve si se escribe lo mismo que ya se
   * rechazó. Antes seguía en pantalla hasta el siguiente envío.
   */
  const [datosDelError, setDatosDelError] = useState<DatosEmisor | null>(null);
  const [bloqueo, setBloqueo] = useState<Bloqueo>(null);
  const [mensaje, setMensaje] = useState<{ tono: "exito" | "error" | "info"; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [casilla, setCasilla] = useState(false);
  const [errorCasilla, setErrorCasilla] = useState<string | null>(null);

  const cargar = useCallback(async (): Promise<void> => {
    setCargando(true);
    setErrorCarga(false);
    const r = await api.obtenerEmisor();
    setCargando(false);
    if (r.ok) {
      setEmisor(r.datos.emisor);
      setDatos(r.datos.emisor === null ? VACIO : datosDe(r.datos.emisor));
    } else if (r.estado === 401) onSesionCaducada();
    else setErrorCarga(true);
  }, [onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // DC-02 A: con facturas emitidas, el NIF identifica la cadena de registros.
  const nifBloqueado = emisor?.taxIdLocked === true;
  const cambiar = (campo: keyof DatosEmisor) => (valor: string) =>
    setDatos((d) => (campo === "postalCode" ? conCodigoPostal(d, valor) : { ...d, [campo]: valor }));
  // Datos opcionales del documento: "" se guarda como "sin dato".
  const opcional = (campo: "contactEmail" | "contactPhone" | "iban" | "documentFooter") => (valor: string) =>
    setDatos((d) => ({ ...d, [campo]: valor === "" ? null : valor }));
  const errorDe = (campo: string): string | null => {
    if (errores[campo] === undefined) return null;
    if (datosDelError !== null && campo in datos && datos[campo as keyof DatosEmisor] !== datosDelError[campo as keyof DatosEmisor]) return null;
    return t.emisor.errores[errores[campo]] ?? t.comun.errorGenerico;
  };

  /** Traduce un rechazo de la API a lo que ve el usuario. */
  function rechazo(r: { estado: number; error: ErrorApi | null }): void {
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 403) return setBloqueo(r.error?.motivo === "MFA_REQUIRED" ? "mfa" : "rol");
    if (r.estado === ESTADO_VALIDACION && r.error !== null) {
      setDatosDelError(datos);
      return setErrores(r.error.campos);
    }
    if (r.error?.motivo === "TAX_ID_LOCKED") return setMensaje({ tono: "error", texto: t.emisor.nifBloqueado });
    if (r.error?.motivo === "SII_EXIT_REQUIRES_SUPPORT") return setMensaje({ tono: "error", texto: t.emisor.siiSalida });
    setMensaje({ tono: "error", texto: r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico });
  }

  async function guardar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setMensaje(null);
    setErrores({});
    setBloqueo(null);
    setOcupado(true);
    const r = await api.guardarEmisor(datos);
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    setEmisor(r.datos.emisor);
    if (r.datos.emisor !== null) setDatos(datosDe(r.datos.emisor));
    setCasilla(false);
    setMensaje({
      tono: r.datos.emisor?.confirmedAt ? "info" : "exito",
      texto: r.datos.emisor?.confirmedAt ? t.emisor.sinCambios : t.emisor.guardado,
    });
    onCambio();
  }

  async function confirmar(): Promise<void> {
    setMensaje(null);
    if (!casilla) {
      setErrorCasilla(t.emisor.faltaCasilla);
      return;
    }
    setErrorCasilla(null);
    setBloqueo(null);
    setOcupado(true);
    const r = await api.confirmarEmisor();
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    setEmisor(r.datos.emisor);
    setCasilla(false);
    onCambio();
  }

  if (cargando) {
    return (
      <Tarjeta titulo={t.emisor.titulo}>
        <p>{t.emisor.cargando}</p>
      </Tarjeta>
    );
  }
  if (errorCarga) {
    return (
      <Tarjeta titulo={t.emisor.titulo}>
        <Aviso tono="error">{t.emisor.error}</Aviso>
        <Boton variante="secundario" onClick={() => void cargar()}>
          {t.emisor.reintentar}
        </Boton>
      </Tarjeta>
    );
  }

  return (
    <>
      <Tarjeta titulo={t.emisor.titulo}>
        <p>{t.emisor.detalle}</p>

        {bloqueo === "mfa" ? (
          <Aviso tono="aviso" titulo={t.emisor.mfaTitulo}>
            {t.emisor.mfaDetalle}
          </Aviso>
        ) : null}
        {bloqueo === "mfa" ? <Boton onClick={onIrASeguridad}>{t.emisor.irASeguridad}</Boton> : null}
        {bloqueo === "rol" ? <Aviso tono="aviso">{t.emisor.soloOwner}</Aviso> : null}

        <form className="formulario-acotado" onSubmit={guardar} noValidate>
          <h3>{t.emisor.bloqueIdentidad}</h3>
          <Campo
            id="emisor-nombre"
            etiqueta={t.emisor.legalName}
            valor={datos.legalName}
            onChange={cambiar("legalName")}
            autoComplete="organization"
            error={errorDe("legalName")}
          />
          <div className="fila-campos">
            <Selector
              id="emisor-tipo"
              etiqueta={t.emisor.taxIdType}
              valor={datos.taxIdType}
              opciones={TIPOS}
              onChange={(v) => setDatos((d) => ({ ...d, taxIdType: v }))}
              error={errorDe("taxIdType")}
              deshabilitado={nifBloqueado}
            />
            <Campo
              id="emisor-nif"
              etiqueta={t.emisor.taxIdValue}
              valor={datos.taxIdValue}
              onChange={cambiar("taxIdValue")}
              autoComplete="off"
              error={errorDe("taxIdValue")}
              soloLectura={nifBloqueado}
              ayuda={nifBloqueado ? t.emisor.nifBloqueado : null}
            />
          </div>
          <h3>{t.emisor.bloqueDomicilio}</h3>
          <Campo
            id="emisor-direccion"
            etiqueta={t.emisor.addressLine}
            valor={datos.addressLine}
            onChange={cambiar("addressLine")}
            autoComplete="street-address"
            error={errorDe("addressLine")}
          />
          <div className="fila-campos">
            <Campo
              id="emisor-cp"
              etiqueta={t.emisor.postalCode}
              valor={datos.postalCode}
              onChange={cambiar("postalCode")}
              inputMode="numeric"
              autoComplete="postal-code"
              error={errorDe("postalCode")}
            />
            <Campo
              id="emisor-municipio"
              etiqueta={t.emisor.city}
              valor={datos.city}
              onChange={cambiar("city")}
              autoComplete="address-level2"
              error={errorDe("city")}
            />
            <Campo
              id="emisor-provincia"
              etiqueta={t.emisor.province}
              valor={datos.province}
              onChange={cambiar("province")}
              autoComplete="address-level1"
              error={errorDe("province")}
            />
          </div>
          <h3>{t.emisor.bloqueFiscalidad}</h3>
          <Selector
            id="emisor-actividad"
            etiqueta={t.emisor.activityNature}
            valor={datos.activityNature}
            opciones={ACTIVIDADES}
            onChange={(v) => setDatos((d) => ({ ...d, activityNature: v }))}
            error={errorDe("activityNature")}
          />
          <p className="ayuda-neutra">{t.emisor.ayudaActividad}</p>

          {/* OD-0035 A: sin responder, el control previo no deja emitir. */}
          <Selector
            id="emisor-sii"
            etiqueta={t.emisor.siiPregunta}
            valor={datos.siiStatus ?? ""}
            opciones={SII}
            onChange={(v) => setDatos((d) => ({ ...d, siiStatus: v === "" ? null : v }))}
            error={errorDe("siiStatus")}
            deshabilitado={emisor?.siiStatus === "SII"}
          />
          <p className="ayuda-neutra">
            {t.emisor.ayudaSii}{" "}
            <a href={t.emisor.enlaceSii} target="_blank" rel="noopener noreferrer">
              {t.emisor.enlaceSiiTexto}
            </a>
          </p>

          {/* OD-0063 D-10c: opcional; lo lee el libro registro de la AEAT. */}
          <h3>{t.emisor.iaeTitulo}</h3>
          <p className="ayuda-neutra">{t.emisor.iaeDetalle}</p>
          <div className="fila-campos">
            <Selector
              id="emisor-iae-codigo"
              etiqueta={t.emisor.iaeCodigo}
              valor={datos.iaeActivityType ?? ""}
              opciones={CODIGOS_IAE}
              // Otro codigo, otra lista de tipos: el "03" de A no es el "03" de B.
              onChange={(v) =>
                setDatos((d) => ({
                  ...d,
                  iaeActivityType: v === "" ? null : v,
                  iaeActivitySubtype: v === d.iaeActivityType ? d.iaeActivitySubtype : null,
                  iaeEpigraph: v === "" ? null : d.iaeEpigraph,
                }))
              }
              error={errorDe("iaeActivityType")}
            />
            {tiposIae(datos.iaeActivityType).length > 0 ? (
              <Selector
                id="emisor-iae-tipo"
                etiqueta={t.emisor.iaeTipo}
                valor={datos.iaeActivitySubtype ?? ""}
                opciones={tiposIae(datos.iaeActivityType)}
                onChange={(v) => setDatos((d) => ({ ...d, iaeActivitySubtype: v === "" ? null : v }))}
                error={errorDe("iaeActivitySubtype")}
              />
            ) : null}
            {datos.iaeActivityType !== null ? (
              <Campo
                id="emisor-iae-epigrafe"
                etiqueta={t.emisor.iaeEpigrafe}
                valor={datos.iaeEpigraph ?? ""}
                onChange={(v) => setDatos((d) => ({ ...d, iaeEpigraph: v === "" ? null : v }))}
                ayuda={t.emisor.iaeAyudaEpigrafe}
                autoComplete="off"
                requerido={datos.iaeActivityType === "A"}
                error={errorDe("iaeEpigraph")}
              />
            ) : null}
          </div>
          {datos.iaeActivityType !== null ? (
            <p className="ayuda-neutra">
              <a href={t.emisor.iaeEnlace} target="_blank" rel="noopener noreferrer">
                {t.emisor.iaeEnlaceTexto}
              </a>
            </p>
          ) : null}
          {pideInmueble(datos) ? <Aviso tono="aviso">{t.emisor.iaeInmueble}</Aviso> : null}

          <h3>{t.emisor.documentoTitulo}</h3>
          <p className="ayuda-neutra">{t.emisor.documentoDetalle}</p>
          <div className="fila-campos">
            <Campo
              id="emisor-email"
              etiqueta={t.emisor.contactEmail}
              valor={datos.contactEmail ?? ""}
              onChange={opcional("contactEmail")}
              tipo="email"
              inputMode="email"
              autoComplete="email"
              requerido={false}
              error={errorDe("contactEmail")}
            />
            <Campo
              id="emisor-telefono"
              etiqueta={t.emisor.contactPhone}
              valor={datos.contactPhone ?? ""}
              onChange={opcional("contactPhone")}
              autoComplete="tel"
              requerido={false}
              error={errorDe("contactPhone")}
            />
          </div>
          <div className="fila-campos">
            <Selector
              id="emisor-forma-pago"
              etiqueta={t.emisor.paymentMethod}
              valor={datos.paymentMethod ?? "NINGUNA"}
              opciones={FORMAS_PAGO}
              onChange={(v) => setDatos((d) => ({ ...d, paymentMethod: v === "NINGUNA" ? null : v }))}
              error={errorDe("paymentMethod")}
            />
            <Campo
              id="emisor-iban"
              etiqueta={t.emisor.iban}
              valor={datos.iban ?? ""}
              onChange={opcional("iban")}
              ayuda={t.emisor.ayudaIban}
              autoComplete="off"
              requerido={false}
              error={errorDe("iban")}
            />
          </div>
          <AreaTexto
            id="emisor-pie"
            etiqueta={t.emisor.documentFooter}
            valor={datos.documentFooter ?? ""}
            onChange={opcional("documentFooter")}
            ayuda={t.emisor.ayudaPie}
            filas={5}
            error={errorDe("documentFooter")}
          />
          {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
          {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}
          <Boton tipo="submit" deshabilitado={ocupado || !hayConexion}>
            {ocupado ? t.emisor.guardando : t.emisor.guardar}
          </Boton>
        </form>
      </Tarjeta>

      {emisor !== null ? (
        <Tarjeta titulo={t.emisor.confirmarTitulo}>
          <p className="ayuda-neutra">{t.emisor.formatoValido}</p>
          <EnlacesComprobarAeat tipo={emisor.taxIdType} />
          {emisor.taxIdControlWarning ? <Aviso tono="aviso">{t.emisor.avisoControl}</Aviso> : null}
          {emisor.confirmedAt !== null ? (
            <Aviso tono="exito">{t.emisor.confirmados(fechaLegible(emisor.confirmedAt))}</Aviso>
          ) : (
            <>
              <Aviso tono="aviso">{t.emisor.confirmarDetalle}</Aviso>
              <Casilla
                id="emisor-casilla"
                etiqueta={t.emisor.casilla}
                marcada={casilla}
                onChange={setCasilla}
                error={errorCasilla}
              />
              <Boton onClick={() => void confirmar()} deshabilitado={ocupado || !hayConexion}>
                {ocupado ? t.emisor.confirmando : t.emisor.confirmar}
              </Boton>
            </>
          )}
        </Tarjeta>
      ) : null}

      {/*
        El logotipo va DESPUES de la confirmacion fiscal y aparte, porque no es
        un dato fiscal: cambiarlo no toca la confirmacion (OD-0015 D-03) ni
        reescribe ninguna factura ya emitida.
      */}
      <TarjetaLogo hayConexion={hayConexion} />
    </>
  );
}
